import React from 'react';
import PropTypes from 'prop-types';
import { reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import Button from '../elements-buttons/Button';
import validateCleaningForm from './forms/validateCleaningForm';
import onSubmitCleaning from './forms/onSubmitCleaning';
import { isEmpty, pickBy } from 'lodash';
import CleaningFields from './CleaningFields';
import cleaningInitialValues from './data/cleaningInitialValues';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import classnames from 'classnames';

const CleaningForm = ({
  error,
  handleSubmit,
  isPosting,
  isEdit,
  hasSelectedCleaning,
  hasEnhancedCleaning,
  spaceId,
  intl,
  ...props
}) => {
  return (
    <form className="flex-basis-100 margin-lg-top" onSubmit={handleSubmit}>
      <div className="ps-row-nested">
        <div className="col-xs-12">
          <div className="flex column">
            <h2>
              <FormattedMessage id="hosting.cleaning_form.health_heading" defaultMessage="Enhanced Health and Safety Measures" />
            </h2>
            <div>
              <FormattedMessage
                id="hosting.cleaning_form.add_cleaning_protocol_text"
                defaultMessage="Fill out the form to add your cleaning protocol and additional health and safety measures to your listing page."
              />
            </div>
            <div className="margin-xl-top flex">
              <img src="https://assets.peerspace.com/health.svg?v=rb" alt={intl.formatMessage({ id: 'hosting.cleaning_form.enhanced_badge_image_alt', defaultMessage: 'enhanced cleaning badge' })} />
              <h5 className="margin-md-left"><FormattedMessage id="hosting.cleaning_form.ehanced_health_heading" defaultMessage="Enhanced Health and Safety Measures" /></h5>
            </div>
            <div className="margin-lg-top margin-xl-bottom">
              <FormattedMessage
                id="hosting.cleaning_form.select_cleaning_category"
                defaultMessage="Select {minimum} from each category below to earn the Enhanced Health and Safety Measures badge. This badge will be displayed on your listings."
                values={{ minimum: <b><FormattedMessage id="hosting.cleaning_form.minimum_category" defaultMessage="at least 1" /></b> }}
              />
            </div>
          </div>
          <hr />
        </div>
      </div>
      <div className="ps-row-nested">
        <div className="col-xs-12 col-sm-11">
          <CleaningFields {...props} />
          <div className="cleaning-badge-section flex middle margin-xl-top">
            <img
              width="42"
              src='https://assets.peerspace.com/health.svg?v=rb'
              alt={ intl.formatMessage({ id: 'hosting.cleaning_form.enhanced_badge_image_alt', defaultMessage: 'enhanced cleaning badge' })}
              className={classnames({ 'filter-gray-300': !hasEnhancedCleaning })}
            />
            <div className={`margin-lg-left ${!hasEnhancedCleaning ? 'text-gray-600' : ''}`}>
              <h6 className="margin-sm-bottom no-top-margin"><FormattedMessage id="hosting.cleaning_form.enhanced_health_badge_heading" defaultMessage="Enhanced Health and Safety Measures badge" /></h6>
              {
                hasEnhancedCleaning ?
                  <div><FormattedMessage id="hosting.cleaning_form.bade_earned_text" defaultMessage="Badge Earned" /></div> :
                  <div><FormattedMessage id="hosting.cleaning_form." defaultMessage="Choose {minimum} from each category" values={{ minimum: <b><FormattedMessage id="hosting.cleaning_form.minimum_enhanced_cleaning_category" defaultMessage="at least 1 item" /></b> }}/></div>
              }
            </div>
          </div>
        </div>
      </div>
      <div className="ps-row-nested">
        <div className="col-xs-12">
          <hr />
          <div className="flex space-between">
            {!isEdit && (
              <a
                className="btn btn-default btn-outlined-light"
                href={`/your-space/${spaceId}/availability`}
              >
                <FormattedMessage
                  id="hosting.cleaning_form.back_link_text"
                  defaultMessage="Back"
                />
              </a>
            )}
            <Button
              category={isEdit ? 'primary' : !hasSelectedCleaning ? 'default' : 'primary' }
              className="submit-cta block-xs-left"
              disabled={isPosting}
              attributes={{
                'data-testing-id': 'submit-cleaning'
              }}
            >
              { isEdit ? <FormattedMessage id="hosting.cleaning_form.button_text_done" defaultMessage="Done" /> : !hasSelectedCleaning ? <FormattedMessage id="hosting.cleaning_form.button_text_skip" defaultMessage="Skip" /> : <FormattedMessage id="hosting.cleaning_form.button_text_next" defaultMessage="Next" /> }
            </Button>
          </div>
        </div>
      </div>
      <div className="text-danger text-small margin-xl-top">{error}</div>
    </form>
  );
};

CleaningForm.propTypes = {
  handleSubmit: PropTypes.func,
  error: PropTypes.string,
  parkingToggleFieldValue: PropTypes.string,
  isPosting: PropTypes.bool,
  isEdit: PropTypes.bool,
  hasSelectedCleaning: PropTypes.bool,
  hasEnhancedCleaning: PropTypes.bool,
  spaceId: PropTypes.string,
  intl: intlShape
};

const config = {
  form: 'EDIT_CLEANING_FORM',
  validate: validateCleaningForm,
  onSubmit: onSubmitCleaning
};
const selector = formValueSelector('EDIT_CLEANING_FORM');

function mapStateToProps (state) {
  const checkForSelectedItems = (item) => {
    const selectedItem = selector(state, item);

    // this removes all the values that are falsey
    return !isEmpty(pickBy(selectedItem, value => value));
  };

  const hasDescription = selector(state, 'description');
  const hasCleaningMeasures = checkForSelectedItems('cleaning_measures');
  const hasProtectiveGear = checkForSelectedItems('protective_gear');
  const hasPhysicalDistance = checkForSelectedItems('physical_distance');
  const hasSignage = checkForSelectedItems('signage');
  const hasEnhancedCleaning = hasCleaningMeasures &&
                              hasProtectiveGear &&
                              hasPhysicalDistance &&
                              hasSignage;
  const hasSelectedCleaning = hasCleaningMeasures ||
                              hasProtectiveGear ||
                              hasPhysicalDistance ||
                              hasSignage ||
                              hasDescription;

  return {
    hasEnhancedCleaning,
    hasSelectedCleaning,
    initialValues: {
      ...cleaningInitialValues(state)
    }
  };
}

export default connect(mapStateToProps)(reduxForm(config)(injectIntl(CleaningForm)));
